import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import type { LayoutContext } from "../components/Layout";
import {
  fetchSettings,
  saveAnniversaries,
  saveOutcome,
  saveWarnDays,
  type Outcome,
  type SettingsDto,
} from "../lib/settingsPage";
import { PageTitle, SectionTitle } from "../components/Heading";
import { OfferInterests } from "../components/OfferInterests";

/**
 * Settings page.
 *
 * The save outcome persists in state, and `saveOutcome` refuses to produce a
 * success message while any entry was rejected, so errors are never hidden.
 *
 * The "Re-seed from YAML" button is deliberately absent -- see
 * `routers/settings.py` and the note in `migration/side-effects.md`. Re-seeding
 * is the `python -m wallet_core.seed` CLI, run from `server/` (2026-09-11).
 */
export default function Settings() {
  // Sign out moved here from the header (restyle, 2026-09-12): a one-person
  // app rarely signs out, and the phone header is brand + gear.
  const { email, onSignOut } = useOutletContext<LayoutContext>();
  const [data, setData] = useState<SettingsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<number, string>>({});
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [warnDays, setWarnDays] = useState("7");
  const [warnSaved, setWarnSaved] = useState(false);

  const load = useCallback(() => {
    fetchSettings()
      .then((d) => {
        setData(d);
        setWarnDays(String(d.warn_days_before));
        setValues(
          Object.fromEntries(
            d.needing_anniversary.map((c) => [c.id, c.anniversary_date]),
          ),
        );
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  useEffect(load, [load]);

  // Rendered whatever the settings fetch did: a down API must not leave you
  // with no way to sign out (restyle, 2026-09-12).
  const account = (
    <section className="space-y-2">
      <SectionTitle>Account</SectionTitle>
      <div className="flex items-center gap-3 rounded-xl bg-neutral-900 px-4 py-3">
        <span className="min-w-0 grow truncate font-mono text-[13px] text-neutral-400">{email ?? "—"}</span>
        <button type="button" onClick={onSignOut} className="btn btn-ghost btn-sm shrink-0">
          Sign out
        </button>
      </div>
    </section>
  );

  if (!data) {
    return (
      <section className="space-y-8">
        <PageTitle>Settings</PageTitle>
        {error ? (
          <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>
        ) : (
          <div className="text-sm text-neutral-400">Loading…</div>
        )}
        {account}
      </section>
    );
  }

  return (
    <section className="space-y-8">
      <PageTitle>Settings</PageTitle>

      <section className="space-y-3">
        <SectionTitle>Card Anniversary Dates</SectionTitle>
        {data.needing_anniversary.length === 0 ? (
          <p className="text-sm text-neutral-400">
            No cards currently use cardmember-year reset schedules.
          </p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              saveAnniversaries(values)
                .then((r) => {
                  setOutcome(saveOutcome(r));
                  // Reload regardless: the valid entries were written even
                  // when others were rejected, so the reference list below is
                  // now stale either way.
                  load();
                })
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : String(err)),
                );
            }}
          >
            {data.needing_anniversary.map((c) => (
              <label
                key={c.id}
                className="flex flex-col gap-1 text-xs text-neutral-400"
              >
                {c.label}
                <input
                  value={values[c.id] ?? ""}
                  placeholder="MM-DD (e.g., 03-15)"
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [c.id]: e.target.value }))
                  }
                  className="min-h-10 w-full max-w-sm rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
                />
              </label>
            ))}
            <button type="submit" className="btn btn-primary">
              Save Anniversary Dates
            </button>
          </form>
        )}

        {outcome && <OutcomeBanner outcome={outcome} />}
      </section>

      <section className="space-y-2">
        <SectionTitle>All Card Dates</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <tbody>
              {data.all_dates.map((r) => (
                <tr key={r.display_name} className="border-t border-neutral-800">
                  <td className="py-2.5 pr-3">{r.display_name}</td>
                  <td className="py-2.5 text-right font-mono text-[13px] text-neutral-400">
                    {r.anniversary_date}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <SectionTitle>Notifications</SectionTitle>
        <p className="text-xs text-neutral-500">
          Email notifications will be available in a future update.
        </p>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setWarnSaved(false);
            saveWarnDays(Number(warnDays))
              .then((v) => {
                setWarnDays(String(v));
                setWarnSaved(true);
              })
              .catch((err: unknown) =>
                setError(err instanceof Error ? err.message : String(err)),
              );
          }}
        >
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            Warn days before credit expires
            <input
              type="number"
              min={1}
              max={30}
              value={warnDays}
              onChange={(e) => setWarnDays(e.target.value)}
              className="min-h-10 w-28 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
            />
          </label>
          <button type="submit" className="btn btn-ghost">
            Save Preferences
          </button>
          {warnSaved && (
            <span className="text-sm text-green-400">Preferences saved.</span>
          )}
        </form>
      </section>

      <OfferInterests />

      <section className="space-y-1">
        <SectionTitle>Data</SectionTitle>
        <p className="text-sm">Total cards: {data.card_count}</p>
        <p className="text-xs text-neutral-500">
          Re-seeding from YAML is an operator task with no button here: from{" "}
          <code>server/</code>, run <code>python -m wallet_core.seed</code>.
        </p>
      </section>

      {account}

      {error && (
        <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          {error}
        </div>
      )}
    </section>
  );
}

function OutcomeBanner({ outcome }: { outcome: Outcome }) {
  if (outcome.kind === "success") {
    return (
      <div className="rounded-md border border-green-900 bg-green-950/30 p-3 text-sm text-green-300">
        {outcome.message}
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-md border border-amber-900 bg-amber-950/30 p-3 text-sm">
      {outcome.kind === "partial" && <p>{outcome.message}</p>}
      <ul className="list-inside list-disc">
        {outcome.errors.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </div>
  );
}
